import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { Adg4Button } from "@/components/adg4/button/src";
import { Picture } from "@/components/wpl/image/src";
export const Team24Promo = () => {
    const intl = useIntl();

    // Localized Strings
    const logoAltTxt = intl.formatMessage({
        id: "HomePageV2.Team24PromoSection.logoAltTxt",
        defaultMessage: "Atlassian team'24 event logo"
    });
    const promoDescription = intl.formatMessage({
        id: "HomePageV2.Team24PromoSection.description",
        defaultMessage:
            "That's a wrap! Catch up on all the highlights and more."
    });
    const eventDates = intl.formatMessage({
        id: "HomePageV2.Team24PromoSection.eventDates",
        defaultMessage: "April 30 - May 2, 2024"
    });
    const eventLocations = intl.formatMessage({
        id: "HomePageV2.Team24PromoSection.eventLocations",
        defaultMessage: "Las Vegas and digitally"
    });
    const ctaTxt = intl.formatMessage({
        id: "HomePageV2.Team24PromoSection.ctaTxt",
        defaultMessage: "Watch on demand"
    });

    return (
        <UFOSegment name="homepage-v2-team24Promo">
            <section id="team-24-promo">
                <div css={containerStyle}>
                    <Picture
                        image={{
                            src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/colored-shapes-left-desktop2.svg",
                            alt: ""
                        }}
                        css={shapesLeftStyle}
                    />
                    <Picture
                        image={{
                            src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/colored-shapes-right-desktop2.svg",
                            alt: ""
                        }}
                        css={shapesRightStyle}
                    />

                    <div css={textContainerStyle}>
                        <div className="wrapper-left" css={wrapperLeftSide}>
                            <div
                                css={logoContainerStyles}
                                className="logo-container"
                            >
                                <Picture
                                    className="logo-desktop"
                                    image={{
                                        src: "//wac-cdn.atlassian.com/misc-assets/webp-images/team-24_attribution-logo-above-left_full-color_RGB.svg",
                                        alt: logoAltTxt
                                    }}
                                />
                            </div>
                            <p css={descriptionStyles} className="description">
                                {promoDescription}
                            </p>
                        </div>
                        <div css={lineStyles} className="line" />
                        <div className="wrapper-right" css={wrapperRightSide}>
                            <div
                                css={inlineContainerStyle}
                                className="inline-container"
                            >
                                <Picture
                                    image={{
                                        src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/calendar-icon.svg",
                                        alt: "A calendar icon"
                                    }}
                                />
                                <p css={pStyle}>{eventDates}</p>
                            </div>
                            <div
                                css={inlineContainerStyle}
                                className="inline-container"
                            >
                                <Picture
                                    image={{
                                        src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/pin-icon.svg",
                                        alt: "A location pin icon"
                                    }}
                                />
                                <p css={pStyle}>{eventLocations}</p>
                            </div>
                            <Adg4Button
                                href="https://events.atlassian.com/team/home"
                                css={buttonStyle}
                                variant="secondary"
                                color="black"
                                dataEventProps={{
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container": "ctaSection",
                                    "data-product-key": "home",
                                    "data-label": ctaTxt,
                                    "data-label-english": "Register today"
                                }}
                            >
                                {ctaTxt}
                            </Adg4Button>
                        </div>
                    </div>
                </div>
            </section>
        </UFOSegment>
    );
};

// Styles
const containerStyle = css({
    background: "#E9F2FE",
    paddingBlock: "46px",
    borderRadius: "22px",
    position: "relative",
    width: "95%",
    maxWidth: "1368px",
    margin: "0 auto",
    overflow: "hidden",

    [media.below.md]: {
        flexDirection: "column"
    },

    [media.below.sm]: {
        width: "100%",
        paddingBlock: "110px 90px",
        borderRadius: "0"
    }
});

const shapesLeftStyle = css({
    position: "absolute",
    insetBlockStart: "0",
    insetInlineStart: "0",

    [media.below.sm]: {
        insetBlockStart: "-25px"
    }
});

const shapesRightStyle = css({
    position: "absolute",
    insetBlockEnd: "0",
    insetInlineEnd: "0",

    [media.below.sm]: {
        insetBlockEnd: "-23px",
        insetInlineEnd: "-48px"
    }
});

const textContainerStyle = css({
    width: "55%",
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",

    [media.below.md]: {
        flexDirection: "column",
        width: "fit-content"
    }
});

const lineStyles = css({
    [media.below.md]: {
        backgroundColor: "#101214",
        width: "159.367px",
        height: "2px",
        margin: "30px auto"
    }
});

const wrapperLeftSide = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "8px",
    paddingInlineEnd: "20px",

    [media.below.md]: {
        alignItems: "center",
        paddingInlineEnd: 0
    }
});

const logoContainerStyles = css({
    width: "280px"
});

const descriptionStyles = css({
    maxWidth: "408px",
    margin: "0",

    [media.below.md]: {
        textAlign: "center"
    },

    [media.below.sm]: {
        maxWidth: "320px"
    }
});

const wrapperRightSide = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "10px",

    [media.below.md]: {
        alignItems: "center",
        gap: token("space.250")
    }
});
const inlineContainerStyle = css({
    display: "flex",
    alignItems: "center",
    height: "48px"
});
const pStyle = css({
    margin: 0,
    paddingInlineStart: "1rem"
});
const buttonStyle = css({
    minWidth: "192px"
});
